import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'

import { longDate } from '../lib/format'
import { BAND, FONT_MONO, INK, PAPER } from '../theme'

export interface GanttSegment {
  start: number
  end: number
  color: string
  label: string
  tooltip?: string
  striped?: boolean
  faded?: boolean
}

interface Props {
  segments: GanttSegment[]
  markers?: { at: number; color: string; label: string; solid?: boolean }[]
  height?: number
}

/**
 * Barra de Gantt ligera, en porcentajes.
 *
 * No se usa librería de charting a propósito: lo único que hace falta es
 * posicionar cajas en una escala temporal, y una dependencia de gráficas
 * pesaría más que este archivo y costaría control fino sobre el detalle que sí
 * importa (el buffer rayado, la línea de hoy).
 *
 * Todo va redondeado a tope: la ventana se lee como una cinta, no como una
 * celda de hoja de cálculo.
 */
export function GanttBar({ segments, markers = [], height = 26 }: Props) {
  return (
    <Box
      sx={{
        position: 'relative',
        height,
        width: '100%',
        bgcolor: BAND,
        borderRadius: 999,
        overflow: 'visible',
      }}
    >
      {segments.map((s, i) => (
        <Tooltip key={i} title={<Box sx={{ whiteSpace: 'pre-line' }}>{s.tooltip ?? s.label}</Box>}>
          <Box
            sx={{
              position: 'absolute',
              left: `${s.start}%`,
              width: `${Math.max(s.end - s.start, 2)}%`,
              top: 0,
              height: '100%',
              borderRadius: 999,
              opacity: s.faded ? 0.4 : 1,
              bgcolor: s.striped ? 'transparent' : s.color,
              // El buffer se dibuja rayado: es tiempo reservado, no trabajo.
              backgroundImage: s.striped
                ? `repeating-linear-gradient(45deg, ${s.color}40 0 5px, transparent 5px 11px)`
                : 'none',
              border: s.striped ? `1px dashed ${s.color}99` : 'none',
              display: 'flex',
              alignItems: 'center',
              px: 1,
              overflow: 'hidden',
              transition: 'width 320ms cubic-bezier(0.2,0.8,0.2,1)',
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: FONT_MONO,
                fontSize: '0.6875rem',
                fontWeight: 500,
                letterSpacing: '0.02em',
                color: s.striped ? s.color : PAPER,
                whiteSpace: 'nowrap',
              }}
            >
              {s.label}
            </Box>
          </Box>
        </Tooltip>
      ))}

      {markers.map((m, i) => (
        <Tooltip key={`m${i}`} title={m.label}>
          <Box
            sx={{
              position: 'absolute',
              left: `${m.at}%`,
              top: -4,
              height: 'calc(100% + 8px)',
              width: m.solid ? 2.5 : 2,
              ml: '-1px',
              bgcolor: m.solid ? m.color : 'transparent',
              backgroundImage: m.solid
                ? 'none'
                : `repeating-linear-gradient(to bottom, ${m.color} 0 3px, transparent 3px 6px)`,
              borderRadius: 999,
              cursor: 'help',
              // Cabecita: distingue un marcador de una barra angosta.
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -3,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: m.color,
              },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  )
}

/** Marcador de "hoy": siempre tinta sólida, nunca color de estado. */
export const TODAY_MARKER_COLOR = INK

/** Posición porcentual de una fecha dentro de la ventana. */
export function pct(date: string, start: string, end: string): number {
  const s = new Date(start).getTime()
  const e = new Date(end).getTime()
  const d = new Date(date).getTime()
  if (e <= s) return 0
  return Math.min(100, Math.max(0, ((d - s) / (e - s)) * 100))
}

export function windowTooltip(label: string, from: string, to: string): string {
  return `${label}: ${longDate(from)} → ${longDate(to)}`
}
